import { api } from './client'

// Stubs for Purchase flows; implement endpoints in Flask to wire these fully.

// POs
export const listPOs = async (params) => {
  const { data } = await api.get('/purchase/pos', { params })
  // Coerce to array in case backend returns an object wrapper or null
  return Array.isArray(data) ? data : (data?.rows || [])
}
export const getPO = async (poId) => (await api.get(`/purchase/po/${poId}`)).data
export const savePO = async (payload, { idempotencyKey } = {}) => {
  const cfg = {}
  if (idempotencyKey) cfg.headers = { 'Idempotency-Key': idempotencyKey }
  const { data } = await api.post('/purchase/po', payload, cfg)
  return data
}
export const closePO = async (poId) => (await api.post(`/purchase/po/${poId}/close`)).data
export const submitPOForApproval = async (poId, note) => (await api.post(`/purchase/po/${poId}/submit-approval`, { note })).data
export const normalizePOApproval = async (poId) => (await api.post(`/purchase/po/${poId}/normalize-approval`)).data
export const scanPOApproval = async (poId) => (await api.get(`/purchase/po/${poId}/approval-scan`)).data

// Receipts
export const listReceipts = async () => (await api.get('/purchase/receipts')).data

// The month-end stock value on plans without Inventory (the periodic method):
// { applies, period_ym, month, end, accounts, book_value, entered, out_of_date, ready, closed, needed, months }
export const getStockValue = async (periodYm) => (await api.get('/purchase/stock-value', { params: periodYm ? { period_ym: periodYm } : {} })).data
export const saveStockValue = async (periodYm, value) => (await api.post('/purchase/stock-value', { period_ym: periodYm, value })).data
export const undoStockValue = async (periodYm) => (await api.post('/purchase/stock-value/undo', { period_ym: periodYm })).data
export const listReceiptsForPO = async (poId) => (await api.get(`/purchase/po/${poId}/receipts`)).data
export const getReceipt = async (rid) => (await api.get(`/purchase/receipts/${rid}`)).data
export const getReceiptProgress = async (rid, productIds = []) => {
  const params = {}
  if (productIds && productIds.length) params.product_ids = productIds.join(',')
  const { data } = await api.get(`/purchase/receipts/${rid}/progress`, { params })
  return data // { receipt_id, rows: [{product_id, sku, name, received, returned, invoiced, remaining}] }
}
export const saveReceipt = async (payload) => (await api.post('/purchase/receipts', payload)).data
export const listReceiptSkuQueue = async (date, days) => {
  const params = {}
  if (date) params.date = date
  if (days) params.days = days
  const { data } = await api.get('/purchase/receipts/sku-queue', { params })
  return data // { date, rows: [{product_id, sku, name, qty}] }
}

// Vendor Returns
export const listVendorReturns = async () => (await api.get('/purchase/returns')).data
export const getVendorReturn = async (id) => (await api.get(`/purchase/returns/${id}`)).data
export const saveVendorReturn = async (payload) => (await api.post('/purchase/returns', payload)).data

// Aliases used by screens
export const listReturns = async () => listVendorReturns()
export const getReturn = async (id) => getVendorReturn(id)

// Receipt availability for vendor returns (per product)
export const getReceiptAvailability = async (receiptId, productIds = []) => {
  const params = { receipt_id: receiptId }
  if (productIds && productIds.length) params.product_ids = productIds.join(',')
  const { data } = await api.get('/purchase/returns/receipt-availability', { params })
  return data // { receipt_id, location_id, availability: { [product_id]: qty } }
}

// Invoices
export const listInvoices = async (status, invoiceType) => (await api.get('/purchase/invoices', { params: { ...(status ? { status } : {}), ...(invoiceType ? { invoice_type: invoiceType } : {}) } })).data
export const getInvoice = async (id) => (await api.get(`/purchase/invoices/${id}`)).data
export const saveInvoice = async (payload) => (await api.post('/purchase/invoices', payload)).data
export const submitInvoice = async (id) => (await api.post(`/purchase/invoices/${id}/submit`)).data
export const invoiceFileUrl = (id) => `/api/purchase/invoices/${id}/file`

// Accounting: vendor invoice matching
export const getInvoiceMatching = async (id) => (await api.get(`/purchase/invoices/${id}/matching`)).data
export const runInvoiceMatch = async (id) => (await api.post(`/purchase/invoices/${id}/match`)).data
export const returnInvoiceToPurchasing = async (id, reason) => (await api.post(`/purchase/invoices/${id}/return`, { reason })).data
export const approveInvoice = async (id) => (await api.post(`/purchase/invoices/${id}/approve`)).data
export const rejectInvoice = async (id, reason) => (await api.post(`/purchase/invoices/${id}/reject`, { reason })).data
/** apAccountCode: one of the company's payables accounts; blank for the default. */
export const postInvoiceToAP = async (id, apAccountCode) => (await api.post(`/purchase/invoices/${id}/post`, apAccountCode ? { ap_account_code: apAccountCode } : {})).data

// Approvals
export const scanPOApprovals = async () => (await api.get('/purchase/po-approvals')).data
export const setPOApproval = async (poId, status, note) => (await api.post('/purchase/po-approvals', { po_id: poId, status, note })).data
export const resyncPOApprovals = async () => (await api.post('/purchase/po-approvals/resync')).data
export const getApprovalSettings = async () => (await api.get('/purchase/approval-settings')).data
export const saveApprovalSettings = async (threshold) => (await api.post('/purchase/approval-settings', { threshold })).data

// AP Ledger / Payments
export const listVendorOpenInvoices = async (vendorId) => (await api.get('/purchase/ap/open', { params: { vendor_id: vendorId } })).data
export const saveAPPayment = async (payload) => (await api.post('/purchase/ap/payments', payload)).data

// What's left to bill on an order, per product (received on all its receipts,
// less returns and the bills already in): the basis for matching an invoice.
export const getPOBilling = async (poId) => (await api.get(`/purchase/po/${poId}/billing`)).data
// { po_id, rows: [{product_id, sku, name, ordered, unit_cost, received, returned, billed, to_bill}], po_total, billed_total }

// Remaining for PO
export const getPORemaining = async (poId, { net } = {}) => {
  const params = {}
  if (net) params.net = 'true'
  const { data } = await api.get(`/purchase/po/${poId}/remaining`, { params })
  return data // [{product_id, sku, name, remaining}]
}

// Unlinked receipts by vendor
export const listUnlinkedReceipts = async (vendorId) => {
  const { data } = await api.get('/purchase/unlinked-receipts', { params: { vendor_id: vendorId } })
  return data
}

// Upload invoice file (multipart) → {file_path}
export const uploadInvoiceFile = async (file) => {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await api.post('/purchase/invoices/upload-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  return data
}

// Eligible POs for vendor returns
export const listReturnEligiblePOs = async () => (await api.get('/purchase/return-eligible-pos')).data

// Vendor product history
export const getVendorProductHistory = async (vendorId) => {
  const { data } = await api.get(`/purchase/vendor/${vendorId}/product-history`)
  return data
}

// PO Additional Costs (Phase 2)
export const getPOAdditionalCosts = async (poId) => {
  const { data } = await api.get(`/purchase/po/${poId}/additional-costs`)
  return data
}

export const savePOAdditionalCosts = async (poId, costs) => {
  const { data } = await api.post(`/purchase/po/${poId}/additional-costs`, { costs })
  return data
}

export const getPOTotals = async (poId) => {
  const { data } = await api.get(`/purchase/po/${poId}/totals`)
  return data // { line_items_total, additional_costs_total, grand_total }
}
